/**
 * Fetch-based SSE reader.
 *
 * Replaces the browser EventSource API so SSE streams travel through apiFetch
 * (session-authenticated via the OIDC cookie; 401s redirect to login).
 * The SSE wire protocol (``event: T\ndata: D\n\n``) is parsed
 * manually from the fetch response body ReadableStream.
 *
 * Usage:
 *
 *   const sse = fetchSSE('/api/s', {
 *     onOpen()  { … },
 *     onEvent(event, data) { … },
 *     onError(err) { … },
 *   });
 *
 *   sse.close();      // permanent shutdown
 *   sse.reconnect();   // close + reopen
 */

import { apiFetch } from './api.js';

export function fetchSSE(url, callbacks = {}) {
  let aborted = false;
  let reader = null;

  const connect = async () => {
    try {
      const resp = await apiFetch(url);
      if (aborted) return;

      if (!resp.ok) {
        callbacks.onError?.(new Error(`SSE connect failed: HTTP ${resp.status}`));
        return;
      }

      callbacks.onOpen?.();

      if (!resp.body) {
        callbacks.onError?.(new Error('SSE response has no readable body'));
        return;
      }

      reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let eventType = 'message';
      let dataLines = [];

      while (!aborted) {
        const { done, value } = await reader.read();
        if (done) {
          callbacks.onError?.(new Error('SSE stream ended'));
          return;
        }

        buffer += decoder.decode(value, { stream: true });

        // Process complete lines; keep the last (potentially
        // incomplete) line in the buffer for the next chunk.
        let idx;
        while ((idx = buffer.indexOf('\n')) !== -1) {
          const line = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 1);

          // Trim trailing carriage return.
          const clean = line.endsWith('\r') ? line.slice(0, -1) : line;

          if (clean === '') {
            // Empty line dispatches the accumulated event.
            if (dataLines.length > 0) {
              callbacks.onEvent?.(eventType, dataLines.join('\n'));
            }
            eventType = 'message';
            dataLines = [];
          } else if (clean.startsWith('event: ')) {
            eventType = clean.slice(7).trim();
          } else if (clean.startsWith('event:')) {
            eventType = clean.slice(6).trim();
          } else if (clean.startsWith('data: ')) {
            dataLines.push(clean.slice(6));
          } else if (clean.startsWith('data:')) {
            dataLines.push(clean.slice(5));
          }
          // Other fields (id:, retry:) are silently ignored.
        }
      }
    } catch (err) {
      if (!aborted) {
        callbacks.onError?.(err);
      }
    }
  };

  connect();

  return {
    close() {
      aborted = true;
      reader?.cancel().catch(() => {});
    },
    reconnect() {
      reader?.cancel().catch(() => {});
      aborted = false;
      connect();
    },
  };
}
